import { useState, useEffect, useCallback } from 'react';
import { PenSquare, Save, Send, EyeOff, CheckCircle2, Clock } from 'lucide-react';
import { apiRequest } from '@/utils/api-request';
import { toastMessage } from '@/utils/toast-message';

const BASE = '/api/parent-app/admin';

type Exam = { id: number; name: string; exam_type: string; class_id_fk: number | null; class_name: string | null };
type Section = { id: number; section_label: string };
type ClassRow = { id: number; name: string };
type Column = { item_type: 'subject' | 'skill'; item_id: number; name: string; max_marks: string | number | null };
type Student = { id: number; display_name: string; roll_number: string | null };
type Score = { item_type: 'subject' | 'skill'; item_id: number; student_id_fk: number; marks_obtained: string | number | null; grade_label: string | null; is_absent: number };
type Grid = {
  exam: { id: number; name: string };
  columns: Column[];
  students: Student[];
  scores: Score[];
  grade_choices: string[];
  release: { status: 'none' | 'pending' | 'published'; published_at: string | null };
  can_publish: boolean;
};

type ClassesData = { grouped?: { classes: { id: number; class_label: string }[] }[] };
function flattenClasses(data: ClassesData): ClassRow[] {
  return (data?.grouped || []).flatMap(g => (g.classes || []).map(c => ({ id: c.id, name: c.class_label })));
}

export default function MarksPage() {
  const [exams, setExams] = useState<Exam[]>([]);
  const [classes, setClasses] = useState<ClassRow[]>([]);
  const [examId, setExamId] = useState<number | ''>('');
  const [classId, setClassId] = useState<number | ''>('');
  const [sections, setSections] = useState<Section[]>([]);
  const [sectionId, setSectionId] = useState<number | ''>('');

  useEffect(() => {
    apiRequest<any>(`${BASE}/exams`).then(r => setExams(r.data || [])).catch(() => {});
    apiRequest<any>(`${BASE}/classes`).then(r => setClasses(flattenClasses(r.data))).catch(() => {});
  }, []);

  const exam = exams.find(e => e.id === examId);
  const effectiveClassId = exam?.class_id_fk ?? (classId || null);

  useEffect(() => {
    setSectionId(''); setSections([]);
    if (!effectiveClassId) return;
    apiRequest<any>(`${BASE}/classes/${effectiveClassId}/sections`)
      .then(r => setSections(r.data?.data || r.data || []))
      .catch(() => {});
  }, [effectiveClassId]);

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', background: 'var(--bg)' }}>
      <div className="page-head">
        <div className="page-head-icon"><PenSquare size={24} style={{ color: '#fff' }} /></div>
        <div className="page-head-text">
          <h1>Marks Entry</h1>
          <p>Enter scores for an exam section, then publish the results to parents</p>
        </div>
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, alignItems: 'flex-end', padding: '16px 20px', borderBottom: '1px solid var(--border)' }}>
        <Labeled label="Exam">
          <select className="select" value={examId} onChange={e => { setExamId(e.target.value ? Number(e.target.value) : ''); setClassId(''); }}>
            <option value="">Select exam…</option>
            {exams.map(e => <option key={e.id} value={e.id}>{e.name}{e.class_name ? ` — ${e.class_name}` : ''}</option>)}
          </select>
        </Labeled>
        {exam && !exam.class_id_fk && (
          <Labeled label="Class">
            <select className="select" value={classId} onChange={e => setClassId(e.target.value ? Number(e.target.value) : '')}>
              <option value="">Select class…</option>
              {classes.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </Labeled>
        )}
        <Labeled label="Section">
          <select className="select" value={sectionId} onChange={e => setSectionId(e.target.value ? Number(e.target.value) : '')} disabled={!sections.length}>
            <option value="">{sections.length ? 'Select section…' : 'Pick a class first'}</option>
            {sections.map(s => <option key={s.id} value={s.id}>Section {s.section_label}</option>)}
          </select>
        </Labeled>
      </div>

      <div style={{ flex: 1, overflow: 'auto', padding: 20 }}>
        {examId && sectionId
          ? <MarksGrid examId={examId as number} sectionId={sectionId as number} />
          : <div className="empty"><div className="empty-art" style={{ background: 'var(--slate-soft)', color: 'var(--slate)' }}><PenSquare size={34} /></div><p>Pick an exam and section to enter marks</p></div>}
      </div>
    </div>
  );
}

function MarksGrid({ examId, sectionId }: { examId: number; sectionId: number }) {
  const [grid, setGrid] = useState<Grid | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [values, setValues] = useState<Record<string, string>>({});   // `${studentId}:${type}:${itemId}` -> value
  const [absent, setAbsent] = useState<Record<number, boolean>>({});
  const [existed, setExisted] = useState<Set<string>>(new Set());
  const [skipped, setSkipped] = useState<Record<string, string>>({});   // key -> reason, for rows the backend rejected

  const key = (s: number, t: string, i: number) => `${s}:${t}:${i}`;

  const load = useCallback(() => {
    setLoading(true);
    apiRequest<any>(`${BASE}/marks/grid?exam_id=${examId}&section_id=${sectionId}`)
      .then(r => {
        const g: Grid = r.data;
        setGrid(g);
        const v: Record<string, string> = {}; const ab: Record<number, boolean> = {}; const ex = new Set<string>();
        for (const s of g.scores) {
          const k = key(s.student_id_fk, s.item_type, s.item_id);
          ex.add(k);
          if (s.is_absent) ab[s.student_id_fk] = true;
          v[k] = s.marks_obtained != null ? String(s.marks_obtained) : (s.grade_label || '');
        }
        setValues(v); setAbsent(ab); setExisted(ex);
      })
      .catch(() => toastMessage('Failed to load grid', 'error'))
      .finally(() => setLoading(false));
  }, [examId, sectionId]);
  useEffect(load, [load]);

  const setVal = (s: number, t: string, i: number, val: string) => {
    setValues(p => ({ ...p, [key(s, t, i)]: val }));
    setSkipped(p => { if (!p[key(s, t, i)]) return p; const n = { ...p }; delete n[key(s, t, i)]; return n; });
  };
  const toggleAbsent = (s: number) => setAbsent(p => ({ ...p, [s]: !p[s] }));

  const save = async () => {
    if (!grid) return;
    setSaving(true);
    const scores: any[] = [];
    for (const st of grid.students) {
      const isAbsent = !!absent[st.id];
      for (const c of grid.columns) {
        const k = key(st.id, c.item_type, c.item_id);
        const val = values[k] ?? '';
        if (!isAbsent && val === '' && !existed.has(k)) continue;
        scores.push({
          student_id: st.id, item_type: c.item_type, item_id: c.item_id,
          marks_obtained: c.item_type === 'subject' ? val : null,
          grade_label: c.item_type === 'skill' ? val : null,
          is_absent: isAbsent,
        });
      }
    }
    try {
      const r = await apiRequest<any>(`${BASE}/marks`, { method: 'POST', body: JSON.stringify({ exam_id: examId, section_id: sectionId, scores }) });
      const rejected: any[] = r.data?.skipped ?? [];
      if (rejected.length) {
        const map: Record<string, string> = {};
        for (const row of rejected) map[key(row.student_id, row.item_type, row.item_id)] = row.reason;
        setSkipped(map);
        toastMessage(`Saved ${r.data?.saved ?? 0}, but ${rejected.length} rejected: ${rejected[0].reason}${rejected.length > 1 ? ` (+${rejected.length - 1} more, see highlighted cells)` : ''}`, 'error');
      } else {
        setSkipped({});
        toastMessage(`Saved ${r.data?.saved ?? scores.length} entries`, 'success');
      }
      load();
    } catch { toastMessage('Could not save marks', 'error'); }
    finally { setSaving(false); }
  };

  const publish = async () => {
    try { await apiRequest(`${BASE}/marks/publish`, { method: 'POST', body: JSON.stringify({ exam_id: examId, section_id: sectionId }) }); toastMessage('Results published to parents', 'success'); load(); }
    catch { toastMessage('Could not publish', 'error'); }
  };
  const unpublish = async () => {
    try { await apiRequest(`${BASE}/marks/unpublish`, { method: 'POST', body: JSON.stringify({ exam_id: examId, section_id: sectionId }) }); toastMessage('Results hidden from parents', 'success'); load(); }
    catch { toastMessage('Could not update', 'error'); }
  };

  if (loading) return <p style={{ color: 'var(--text-2)' }}>Loading grid…</p>;
  if (!grid) return null;
  if (!grid.students.length) return <p style={{ color: 'var(--text-2)' }}>No students in this section.</p>;
  if (!grid.columns.length) return <p style={{ color: 'var(--text-2)' }}>This exam has no subjects yet. Add subjects to it in the Exams screen.</p>;

  const published = grid.release.status === 'published';

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <span className="badge" style={{ background: published ? 'var(--success-soft)' : 'var(--warning-soft)', color: published ? 'var(--success)' : 'var(--warning)' }}>
          {published ? <CheckCircle2 size={13} /> : <Clock size={13} />}
          {published ? 'Published to parents' : grid.release.status === 'pending' ? 'Not published' : 'Not saved yet'}
        </span>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          <button className="btn btn-primary" onClick={save} disabled={saving} style={{ gap: 6 }}><Save size={16} /> {saving ? 'Saving…' : 'Save'}</button>
          {grid.can_publish && (published
            ? <button className="btn btn-ghost" onClick={unpublish} style={{ gap: 6 }}><EyeOff size={16} /> Unpublish</button>
            : <button className="btn btn-primary" onClick={publish} style={{ gap: 6 }}><Send size={16} /> Publish</button>)}
        </div>
      </div>
      {!grid.can_publish && !published && (
        <p style={{ color: 'var(--text-2)', fontSize: 12.5, marginBottom: 12 }}>You can enter marks; a staff member with publish permission will release the results to parents.</p>
      )}

      <div style={{ overflowX: 'auto', border: '1px solid var(--border)', borderRadius: 12 }}>
        <table style={{ minWidth: 720, borderCollapse: 'collapse', width: '100%', fontSize: 13.5 }}>
          <thead>
            <tr>
              <th style={{ ...th, position: 'sticky', left: 0, background: 'var(--surface-2)', textAlign: 'left', minWidth: 240 }}>Student</th>
              <th style={{ ...th, minWidth: 88 }}>Absent</th>
              {grid.columns.map(c => (
                <th key={`${c.item_type}-${c.item_id}`} style={{ ...th, minWidth: 132 }}>
                  <div style={{ fontWeight: 700, fontSize: 13.5, color: 'var(--text)' }}>{c.name}</div>
                  <div style={{ fontSize: 11.5, color: 'var(--text-3)', fontWeight: 500, marginTop: 3 }}>
                    {c.item_type === 'subject' ? `out of ${c.max_marks ?? '—'}` : 'skill · grade'}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {grid.students.map(st => {
              const isAbsent = !!absent[st.id];
              return (
                <tr key={st.id}>
                  <td style={{ ...td, position: 'sticky', left: 0, background: 'var(--surface)', textAlign: 'left', fontWeight: 600 }}>
                    {st.roll_number ? <span style={{ color: 'var(--text-3)', marginRight: 8 }}>{st.roll_number}</span> : null}{st.display_name}
                  </td>
                  <td style={{ ...td, textAlign: 'center' }}>
                    <input type="checkbox" checked={isAbsent} onChange={() => toggleAbsent(st.id)} style={{ width: 17, height: 17, cursor: 'pointer' }} />
                  </td>
                  {grid.columns.map(c => {
                    const k = key(st.id, c.item_type, c.item_id);
                    const rejectReason = skipped[k];
                    return (
                      <td key={`${c.item_type}-${c.item_id}`} style={td} title={rejectReason}>
                        {isAbsent
                          ? <span style={{ color: 'var(--text-3)', fontSize: 12 }}>AB</span>
                          : c.item_type === 'skill'
                            ? <select className="select" style={{ ...selCell, ...(rejectReason ? rejectedStyle : {}) }} value={values[k] ?? ''} onChange={e => setVal(st.id, c.item_type, c.item_id, e.target.value)}>
                                <option value="">—</option>
                                {grid.grade_choices.map(o => <option key={o} value={o}>{o}</option>)}
                              </select>
                            : <input className="input" type="number" style={{ ...inCell, ...(rejectReason ? rejectedStyle : {}) }} max={Number(c.max_marks) || undefined} value={values[k] ?? ''} onChange={e => setVal(st.id, c.item_type, c.item_id, e.target.value)} />}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Labeled({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="field" style={{ gap: 4 }}>
      <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-2)' }}>{label}</span>
      {children}
    </label>
  );
}

const th: React.CSSProperties = { padding: '14px 16px', fontSize: 13.5, color: 'var(--text-2)', borderBottom: '1px solid var(--border)', textAlign: 'center', verticalAlign: 'bottom' };
const td: React.CSSProperties = { padding: '12px 16px', borderBottom: '1px solid var(--divider)', textAlign: 'center', fontSize: 13.5 };
const inCell: React.CSSProperties = { width: 92, height: 40, textAlign: 'center', padding: '0 10px', fontSize: 13.5 };
const selCell: React.CSSProperties = { width: 92, height: 40, padding: '0 10px', fontSize: 13.5 };
const rejectedStyle: React.CSSProperties = { borderColor: 'var(--danger)', boxShadow: '0 0 0 1px var(--danger)' };
